import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ToastItem from "./ToastItem";
import { ToastContext } from "./ToastContext";
import "./toasts.css";

/** Wspólna kolejka systemowych komunikatów, niezależna od zmiany strony. */
export default function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const sequence = useRef(0);
  const pending = useRef(new Map());
  const dismiss = useCallback((id, accepted = false) => {
    pending.current.get(id)?.(accepted);
    pending.current.delete(id);
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);
  const notify = useCallback((message, variant = "success") => {
    if (!message) return;
    const id = ++sequence.current;
    setItems((current) => [
      ...current.filter(
        (item) =>
          item.confirmation ||
          item.message !== message ||
          item.variant !== variant,
      ),
      { id, message, variant },
    ]);
  }, []);
  const requestConfirmation = useCallback(
    (message, { acceptLabel = "Potwierdź", cancelLabel = "Anuluj" } = {}) => {
      const id = ++sequence.current;
      const result = new Promise((resolve) => pending.current.set(id, resolve));
      setItems((current) => [
        ...current,
        {
          id,
          message,
          variant: "warning",
          confirmation: true,
          acceptLabel,
          cancelLabel,
        },
      ]);
      return { id, result };
    },
    [],
  );
  useEffect(() => {
    const confirmations = pending.current;
    return () => {
      confirmations.forEach((resolve) => resolve(false));
      confirmations.clear();
    };
  }, []);
  const value = useMemo(
    () => ({ notify, requestConfirmation, dismiss }),
    [notify, requestConfirmation, dismiss],
  );
  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <section className="system-toasts" aria-label="Powiadomienia">
          {items.map((item) => (
            <ToastItem key={item.id} item={item} dismiss={dismiss} />
          ))}
        </section>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
